import { fetchNewPartnerById } from 'legacy/lut/services/partner/fetchNewPartnerById'
import { fetchPartnerRequestById } from 'legacy/lut/services/partner/fetchPartnerRequestById'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { IPartner } from 'clickNgo/entities/partner/models'
import { ContactCreatorContent } from 'clickNgo/features/contact'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'

import { TContactEditorHandles } from '../../../models'

import { getDefaultReplacementContact } from './helpers/getDefaultReplacementContact'

type Props = Pick<TContactEditorHandles, 'onClose' | 'onReplacementContactCreate'> & {
  contact: IContact
  source?: string
}

export const WrappedCreateReplacementContact = memo<Props>(
  ({ contact, onClose, onReplacementContactCreate, source }) => {
    const [partnerData, setPartnerData] = useState<IPartner | null>(null)

    const initialValues = useMemo<Partial<IContact>>(
      () => getDefaultReplacementContact(contact),
      [contact]
    )

    useEffect(() => {
      let isCancelled = false

      setPartnerData(null)

      const fetchPartner =
        contact.partnerId.startsWith('new-') || contact.partnerId.startsWith('portal-')
          ? fetchNewPartnerById
          : fetchPartnerRequestById

      fetchPartner(contact.partnerId, {
        cacheKey: `replacement-contact-${contact.partnerId}`,
      }).then((partner) => {
        if (!isCancelled) {
          setPartnerData(partner ?? null)
        }
      })

      return () => {
        isCancelled = true
      }
    }, [contact.partnerId])

    const handleSubmit = useCallback(
      async (newContact: IContact) => {
        await onReplacementContactCreate?.(newContact)
        await onClose()
      },
      [onClose, onReplacementContactCreate]
    )

    return (
      <>
        <LoaderOverlay isVisible={!partnerData} color="white" />
        <RenderWhen condition={Boolean(partnerData)}>
          <ContactCreatorContent
            key={`${contact.contactId}-${contact.entryId379 ?? 'no-379'}`}
            partner={partnerData!}
            onClose={onClose}
            onCreate={handleSubmit}
            onReactivate={handleSubmit}
            initialValues={initialValues}
            isReplacementContact={true}
            source={source}
          />
        </RenderWhen>
      </>
    )
  }
)

WrappedCreateReplacementContact.displayName = 'WrappedCreateReplacementContact'
